import React, { useMemo, useState } from 'react';
import {
  DataGrid,
  Feedback,
  GridColDef,
  Input,
  Toggle,
} from '@fashionnovateam/nova-ui-toolkit';
import { includes, values } from 'lodash';
import { PurchaseOrderLine } from '../../../Lib/Types/purchase-order';
import {
  FormErrors,
  PurchaseOrderRevisionForm,
  UpdatePurchaseOrderRevisionForm,
} from './validation';
import { QuantityId } from './formTargetId';
import { ErrorWithIcon } from './ErrorWithIcon';

type QuantitiesFormProps = {
  item: NonNullable<PurchaseOrderRevisionForm['items']>[0];
  index: number;
  lines: PurchaseOrderLine[];
  styleColor: string;
  name: string;
  updateForm: UpdatePurchaseOrderRevisionForm;
  disabled: boolean;
  errors: FormErrors | undefined;
};

const columns: GridColDef[] = [
  {
    field: 'size',
    headerName: 'Size',
    render: 'custom',
    flex: 1,
    sortable: false,
    minWidth: 61,
    disableColumnMenu: true,
  },
  {
    field: 'new_quantity',
    headerName: 'New Qty',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 141,
    disableColumnMenu: true,
  },
  {
    field: 'current_quantity',
    headerName: 'Current Qty',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    minWidth: 97,
    disableColumnMenu: true,
  },
  {
    field: 'quantity_difference',
    headerName: 'Qty Diff',
    render: 'custom',
    sortable: true,
    flex: 2,
    minWidth: 97,
    disableColumnMenu: true,
  },
  {
    field: 'quantity_difference_percentage',
    headerName: 'Qty Diff %',
    render: 'custom',
    sortable: false,
    flex: 1,
    minWidth: 97,
    disableColumnMenu: true,
  },
];

type PurchaseOrderRevisionSizes = NonNullable<
  NonNullable<PurchaseOrderRevisionForm['items']>[number]['sizes']
>[number][];

export function QuantitiesForm(props: QuantitiesFormProps) {
  const { item, index, styleColor, name, lines, updateForm, errors, disabled } =
    props;

  const [formPrevValue, setFormPrevValue] =
    useState<PurchaseOrderRevisionSizes>([]);

  const rows = useMemo(
    () =>
      lines.map((line) => {
        const sizeIndex = item.sizes?.findIndex(
          (size) => size.line_item_id === line.po_line_id,
        );
        const newQuantity = item.sizes?.find(
          (size) => size.line_item_id === line.po_line_id,
        )?.new_quantity;
        const currentQuantity = line.quantity;
        const quantityDifference =
          newQuantity !== undefined && newQuantity - currentQuantity;
        const quantityDifferencePercentage =
          newQuantity !== undefined
            ? ((newQuantity - currentQuantity) / currentQuantity) * 100
            : Infinity;

        return {
          id: line.po_line_id.toString(),
          size: <span className="text-xsm text-base-primary">{line.size}</span>,
          new_quantity: (
            <Input
              type="number"
              value={newQuantity ?? ''}
              onChange={(e) => {
                updateForm((prev) => ({
                  ...prev,
                  items: prev.items?.map((prevItem) => {
                    if (prevItem.style_color !== styleColor) return prevItem;
                    if (!e.target.value) {
                      return {
                        ...prevItem,
                        sizes: prevItem.sizes?.filter(
                          (size) => size.line_item_id !== line.po_line_id,
                        ),
                      };
                    }
                    if (
                      prevItem.sizes?.find(
                        (size) => size.line_item_id === line.po_line_id,
                      )
                    ) {
                      return {
                        ...prevItem,
                        sizes: prevItem.sizes?.map((size) => {
                          if (size.line_item_id === line.po_line_id) {
                            return {
                              ...size,
                              new_quantity: Number(e.target.value) || 0,
                            };
                          }
                          return size;
                        }),
                      };
                    }
                    return {
                      ...prevItem,
                      sizes: [
                        ...(prevItem.sizes || []),
                        {
                          line_item_id: line.po_line_id,
                          new_quantity: Number(e.target.value) || 0,
                        },
                      ],
                    };
                  }),
                }));
              }}
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
              error={
                !!errors?.[`items.${index}.sizes.${sizeIndex}.new_quantity`]
              }
              disabled={disabled}
            />
          ),
          current_quantity: (
            <span
              className={
                newQuantity && newQuantity !== line.quantity
                  ? 'text-xsm text-base-disabled line-through decoration-base-disabled'
                  : ''
              }
            >
              {line.quantity}
            </span>
          ),
          quantity_difference: (
            <span className="text-xsm text-base-disabled">
              {quantityDifference || '-'}
            </span>
          ),
          quantity_difference_percentage: (
            <span className="text-xsm text-base-disabled">
              {newQuantity !== undefined &&
              quantityDifferencePercentage !== Infinity
                ? `${quantityDifferencePercentage.toFixed(2)}%`
                : '-'}
            </span>
          ),
        };
      }),
    [disabled, item.sizes, lines, styleColor, updateForm, errors],
  );

  const getSizes = (
    checked: boolean,
    sizes: PurchaseOrderRevisionSizes | undefined,
  ) => {
    if (checked) {
      return formPrevValue;
    }
    if (sizes !== undefined) {
      setFormPrevValue(sizes);
    }
    return undefined;
  };

  return (
    <div id={`${QuantityId}-${index}`}>
      <div className="flex items-center gap-3">
        <Toggle
          onChange={(e) =>
            updateForm((prev) => ({
              ...prev,
              items: prev.items?.map((prevItem) =>
                prevItem.style_color === styleColor
                  ? {
                      ...prevItem,
                      sizes: getSizes(e.target.checked, prevItem.sizes),
                    }
                  : prevItem,
              ),
            }))
          }
          checked={!!item.sizes}
          disabled={disabled}
        />
        <span className="text-sm font-medium text-base-primary">
          Quantity Change
        </span>
      </div>
      {item.sizes ? (
        <>
          <div className="flex flex-col gap-1 py-3">
            <p className="text-sm font-medium text-base-primary">{name}</p>
            <Feedback
              type="info"
              title="Please enter the units you would like to ship to us for each size."
            />
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <DataGrid
              hideFilterButton
              hideGroupButton
              hideFooterPagination
              hideFooter
              hideSortButton
              rowHeight={56}
              rows={rows}
              columns={columns}
            />
          </div>
          {includes(values(errors), 'negative_qty') && (
            <Feedback
              type="error"
              title="You cannot enter a negative quantity."
            />
          )}
          {errors?.[`items.${index}.sizes`] && (
            <ErrorWithIcon error={errors?.[`items.${index}.sizes`]} />
          )}
        </>
      ) : null}
    </div>
  );
}
